import { useState, type ReactNode } from 'react';
import { cn } from '../cn.js';
import { useDrawIn } from './internal/useDrawIn.js';
import { ChartTooltip } from './internal/ChartTooltip.js';

export interface DonutChartSegment {
  label: string;
  value: number;
  /** A token reference, e.g. "var(--success)" — never a literal colour. */
  color: string;
}

export interface DonutChartProps {
  segments: DonutChartSegment[];
  /** Center label above the total, e.g. "Total". Ignored when `centerValue` is set. */
  centerLabel?: string;
  /** Defaults to the sum of every segment's value. */
  total?: number;
  /**
   * Overrides the default "small caption above / big total below" center with a custom big value
   * (e.g. a "3/4"-style ratio) followed by a small `centerCaption` underneath it — for callers that
   * need a ratio-style center rather than a generic "Total" + raw count. When omitted (the default),
   * the center renders exactly as before: `centerLabel` small on top, `sum` big below — so every
   * existing caller is pixel-identical.
   */
  centerValue?: ReactNode;
  /** Small caption below `centerValue`. Ignored unless `centerValue` is set. */
  centerCaption?: ReactNode;
  size?: number;
  strokeWidth?: number;
  legend?: boolean;
  /** Formats a segment's raw value for the hover tooltip. Defaults to the raw number — every
   *  current caller counts whole items (goals, feedback requests), so no formatting is needed by
   *  default. */
  valueFormat?: (value: number) => string;
  className?: string;
  ariaLabel?: string;
}

// The ring sits inside a fixed 0-100 viewBox, inset from its edge by half the stroke width plus
// this margin — so a thicker `strokeWidth` (and the depth drop-shadow's blur, which extends past
// the stroke's own geometry) never gets clipped by the SVG's default `overflow: hidden`. The old
// fixed radius (42, paired with a 16 default strokeWidth) left literally zero margin — its outer
// edge sat exactly ON the viewBox boundary — so both real callers of this component, which already
// pass strokeWidth={20}, were silently clipped by 2 viewBox units on every side.
const RADIUS_MARGIN = 3;
// The arc draw-in sweep — long enough on a donut of real goal/feedback-status data to read as a
// deliberate reveal, short enough not to feel like a loading spinner.
const DRAW_IN_DURATION = '600ms';
// Per-segment stagger so a multi-segment donut reads as segments sweeping in one after another
// rather than the whole ring snapping in at once.
const DRAW_IN_STAGGER = 60;

/**
 * A multi-segment donut/ring breakdown — Goal Portfolio Status, Feedback Status, anything counting
 * a small set of categories that sum to a whole. Same hand-rolled-SVG reasoning as `LineChart`/
 * `Ring`: no `<defs>`, viewBox-scaled to 100% width so it reflows with its container instead of
 * needing a measured pixel size, and zero-value segments are dropped rather than drawn as an
 * invisible sliver that still eats space in the legend.
 */
export function DonutChart({
  segments,
  centerLabel = 'Total',
  total,
  centerValue,
  centerCaption,
  size = 160,
  strokeWidth = 20,
  legend = true,
  valueFormat = (value) => String(value),
  className,
  ariaLabel = 'Breakdown chart',
}: DonutChartProps) {
  const [drawInRef, mounted] = useDrawIn<HTMLDivElement>();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const drawn = segments.filter((s) => s.value > 0);
  const sum = total ?? segments.reduce((acc, s) => acc + s.value, 0);
  const center = 50;
  // Derived from `strokeWidth` rather than a fixed constant — see `RADIUS_MARGIN` above.
  const radius = Math.max(1, center - strokeWidth / 2 - RADIUS_MARGIN);
  const circumference = 2 * Math.PI * radius;

  let cursor = 0; // fraction of the circle consumed so far, 0-1
  const arcs = drawn.map((s) => {
    const fraction = sum > 0 ? s.value / sum : 0;
    const dash = fraction * circumference;
    // Rotate so each arc starts where the previous one ended, and the whole ring starts at 12
    // o'clock (SVG circles start at 3 o'clock, hence the -90deg correction) rather than 3 o'clock.
    const rotation = cursor * 360 - 90;
    cursor += fraction;
    return { ...s, fraction, dash, rotation };
  });
  const active = activeIndex != null ? arcs[activeIndex] : undefined;
  // Tooltip anchor — the midpoint angle of the hovered arc, converted to a point on the ring in
  // the SAME 0-100 viewBox space `ChartTooltip` expects as x/y percentages (the wrapper below is a
  // square, so viewBox units already equal percent-of-container 1:1).
  const activeMidAngleDeg = active ? active.rotation + (active.fraction * 360) / 2 : 0;
  const activeMidRad = (activeMidAngleDeg * Math.PI) / 180;
  const tooltipX = center + radius * Math.cos(activeMidRad);
  const tooltipY = center + radius * Math.sin(activeMidRad);

  return (
    <div ref={drawInRef} className={cn('flex flex-col items-center gap-3', className)}>
      <div className="relative aspect-square w-full" style={{ maxWidth: size }}>
        <svg viewBox="0 0 100 100" width="100%" height="100%" role="img" aria-label={ariaLabel}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={strokeWidth}
          />
          {arcs.map((arc, i) => (
            <circle
              key={arc.label}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeLinecap={arcs.length > 1 ? 'butt' : 'round'}
              transform={`rotate(${arc.rotation} ${center} ${center})`}
              // `fill="none"` means the shape itself has no hit area — only the stroked band does,
              // and only with this explicit opt-in (SVG's default `pointer-events` for a shape
              // ignores a `none` fill's hit area entirely).
              pointerEvents="stroke"
              onPointerEnter={() => setActiveIndex(i)}
              onPointerLeave={() => setActiveIndex((current) => (current === i ? null : current))}
              // Arc draw-in + depth. `strokeDasharray` is fixed at [circumference, circumference]
              // — a potential full-circle "on" stretch, same technique `Ring.tsx` uses — rather
              // than this arc's own [dash, gap], so `strokeDashoffset` can animate the VISIBLE
              // length of the arc growing from 0 up to `arc.dash`, anchored at this circle's own
              // rotated start point, instead of merely sliding an already-fixed-length dash around
              // (which a [dash, gap] pattern — one period long, with no room for the arc to be
              // shorter than its target inside that pattern — cannot do). Unmounted, the offset
              // equals the full circumference: the entire "on" stretch is pushed past the visible
              // path, so nothing is drawn. `drop-shadow`, not a plain `fill`/`stroke` colour, so
              // the low-opacity dark value is a legitimate exception to the token-colour rule.
              strokeDasharray={`${circumference} ${circumference}`}
              strokeDashoffset={mounted ? circumference - arc.dash : circumference}
              opacity={activeIndex == null || activeIndex === i ? 1 : 0.55}
              style={{
                transition: `stroke-dashoffset ${DRAW_IN_DURATION} var(--ease), opacity 150ms var(--ease)`,
                transitionDelay: `${i * DRAW_IN_STAGGER}ms`,
                filter: 'drop-shadow(0 2px 3px var(--shadow-color))',
                cursor: 'default',
              }}
            />
          ))}
        </svg>
        {active && (
          <ChartTooltip x={tooltipX} y={tooltipY}>
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block size-2 rounded-full"
                style={{ backgroundColor: active.color }}
              />
              <span className="font-semibold text-fg">{active.label}</span>
            </div>
            <div className="text-fg-subtle">
              {valueFormat(active.value)}
              {sum > 0 && (
                <span className="tabular-nums"> · {Math.round((active.value / sum) * 100)}%</span>
              )}
            </div>
          </ChartTooltip>
        )}
        {/* pointer-events-none is load-bearing: this covers the ENTIRE square bounding box (not
            just the donut's hollow center), and being the last element in the stacking order it
            would otherwise intercept every hover/pointer event over the ring itself, silently
            preventing the arcs' own onPointerEnter/onPointerLeave below from ever firing. */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          {centerValue !== undefined ? (
            <>
              <span className="font-display text-2xl font-bold tabular-nums text-fg">
                {centerValue}
              </span>
              {centerCaption != null && (
                <span className="text-xs font-medium text-fg-subtle">{centerCaption}</span>
              )}
            </>
          ) : (
            <>
              <span className="text-xs font-medium text-fg-subtle">{centerLabel}</span>
              <span className="font-display text-2xl font-bold tabular-nums text-fg">{sum}</span>
            </>
          )}
        </div>
      </div>
      {legend && (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
          {segments.map((s) => {
            const percent = sum > 0 ? Math.round((s.value / sum) * 100) : 0;
            return (
              <div key={s.label} className="flex items-center gap-1.5 text-xs text-fg-subtle">
                <span
                  className="inline-block size-2 shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span>
                  {s.label} <span className="tabular-nums text-fg-muted">({percent}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
